import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useApp } from "../context/AppContext";

// Lost Backlinks module — overview + table + detail panel.
// Manual-only sync (no cron yet), Ahrefs all-time backfill on demand.
export default function LostBacklinksView() {
  const { proj, activeId, apiFetch, t, dark, card, btnP, btnG, btnSm, stitle, muted, flash, inp } = useApp();
  if (!proj) return null;

  const [status, setStatus] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [top10, setTop10] = useState([]);
  const [list, setList] = useState({ items: [], total: 0 });
  const [filters, setFilters] = useState({ status: "", reason: "", dr_min: "", search: "", sort: "dr_desc" });
  const [selected, setSelected] = useState(null);
  const [pulling, setPulling] = useState(false);
  const [task, setTask] = useState(null);
  const [analyzing, setAnalyzing] = useState(null);
  const [generating, setGenerating] = useState(null);
  const [analyzingAll, setAnalyzingAll] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [page, setPage] = useState(0);
  const [disavowOpen, setDisavowOpen] = useState(false);
  const [disavowRows, setDisavowRows] = useState(null); // null = loading
  const [disavowSel, setDisavowSel] = useState({});
  const [disavowCachedAt, setDisavowCachedAt] = useState(null);
  const PAGE = 50;

  const loadStatus = useCallback(async () => {
    try { setStatus(await (await apiFetch(`/api/projects/${activeId}/lost-backlinks/status`)).json()); }
    catch (e) { flash("Помилка статусу: " + e.message); }
  }, [activeId, apiFetch, flash]);

  const loadCalendar = useCallback(async () => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/lost-backlinks/calendar`);
      setCalendar(await r.json());
    } catch {}
  }, [activeId, apiFetch]);

  const loadReasons = useCallback(async () => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/lost-backlinks/reasons-summary`);
      const d = await r.json();
      setReasons(d.reasons || []);
    } catch {}
  }, [activeId, apiFetch]);

  const loadTop = useCallback(async () => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/lost-backlinks/top?n=10`);
      const d = await r.json();
      setTop10(d.items || []);
    } catch {}
  }, [activeId, apiFetch]);

  const loadList = useCallback(async () => {
    const qs = new URLSearchParams({
      limit: PAGE, offset: page * PAGE, sort: filters.sort,
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.reason ? { reason: filters.reason } : {}),
      ...(filters.dr_min ? { dr_min: filters.dr_min } : {}),
      ...(filters.search ? { search: filters.search } : {}),
    }).toString();
    try {
      const r = await apiFetch(`/api/projects/${activeId}/lost-backlinks/list?${qs}`);
      setList(await r.json());
    } catch {}
  }, [activeId, apiFetch, filters, page]);

  useEffect(() => { loadStatus(); loadCalendar(); loadReasons(); loadTop(); }, [loadStatus, loadCalendar, loadReasons, loadTop]);
  useEffect(() => { loadList(); }, [loadList]);

  const exportCsv = async (days) => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/lost-backlinks/export.csv?days=${days}`);
      if (!r.ok) { flash("Помилка експорту: " + r.status); return; }
      const blob = await r.blob();
      // Try to read filename from Content-Disposition; fallback to template.
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `lost-backlinks-${days}d-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
      flash(`✓ Експорт ${days} днів — ${(blob.size / 1024).toFixed(1)} KB`);
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const openDisavow = async (refresh = false) => {
    if (refresh && !window.confirm("Перетягнути токсичні беклінки з Ahrefs заново? Витратить Ahrefs units.")) return;
    setDisavowOpen(true); setDisavowRows(null);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/lost-backlinks/spam-backlinks${refresh ? "?refresh=1" : ""}`);
      if (!r.ok) { flash("Помилка: " + r.status); if (!refresh) setDisavowOpen(false); return; }
      const d = await r.json();
      const rows = d.rows || [];
      setDisavowRows(rows);
      setDisavowCachedAt(d.cached_at || null);
      const sel = {}; rows.forEach(x => { sel[x.domain] = true; });
      setDisavowSel(sel);
    } catch (e) { flash("Помилка: " + e.message); if (!refresh) setDisavowOpen(false); }
  };

  const downloadDisavowFile = () => {
    const chosen = (disavowRows || []).filter(x => disavowSel[x.domain]).map(x => x.domain);
    if (!chosen.length) { flash("Не вибрано жодного домену"); return; }
    let bare = (proj.domain || "").replace("https://", "").replace("http://", "").replace("www.", "");
    const sl = bare.indexOf("/"); if (sl >= 0) bare = bare.slice(0, sl);
    const dateStr = new Date().toISOString().slice(0, 10);
    const lines = [
      `# Disavow file for ${bare}`,
      `# Generated by Spalah on ${dateStr}`,
      `# Source: Ahrefs is_spam = true (live links), ${chosen.length} spam domains`,
      `# Upload at: https://search.google.com/search-console/disavow-links`,
      `#`,
      ...chosen.map(d => `domain:${d}`),
    ];
    const out = lines.join("\n") + "\n";
    const slug = bare.replace(/[^a-z0-9.]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase();
    const blob = new Blob([out], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `disavow-${slug}-${dateStr}.txt`;
    document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
    flash(`✓ Disavow — ${chosen.length} доменів`);
    setDisavowOpen(false);
  };

  const startBackfill = async (force = false) => {
    if (pulling) return;
    const alreadyDone = (status?.counts?.total || 0) > 0;
    if (alreadyDone && !force) return;
    const msg = alreadyDone
      ? `Перезапустити backfill? У БД вже ${status?.counts?.total} lost-посилань. Нові додадуться, існуючі НЕ оновляться.`
      : "Запустити backfill всіх lost-посилань через Ahrefs? Витратить ~3K units.";
    if (!window.confirm(msg)) return;
    setPulling(true);
    setTask({ phase: "starting" });
    try {
      const r = await apiFetch(`/api/projects/${activeId}/lost-backlinks/backfill`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) { flash("Помилка: " + (d.error || "")); setPulling(false); setTask(null); return; }
      const taskId = d.taskId;
      const poll = setInterval(async () => {
        try {
          const sR = await apiFetch(`/api/tasks/${taskId}`);
          const s = await sR.json();
          setTask({ ...(s.progress || {}), status: s.status });
          if (s.status === "completed" || s.status === "failed") {
            clearInterval(poll); setPulling(false);
            await Promise.all([loadStatus(), loadCalendar(), loadReasons(), loadTop(), loadList()]);
            flash(s.status === "completed" ? `✓ Backfill: ${(s.progress || {}).inserted || 0} нових lost` : "Помилка: " + (s.error || ""));
            setTimeout(() => setTask(null), 5000);
          }
        } catch {}
      }, 2000);
    } catch (e) { flash("Помилка: " + e.message); setPulling(false); setTask(null); }
  };

  const analyzeReason = async (lbId) => {
    setAnalyzing(lbId);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/lost-backlinks/${lbId}/analyze-reason`, { method: "POST" });
      const d = await r.json();
      if (d.ok) { flash(`Причина: ${d.reason}`); await loadList(); await loadReasons(); }
      else flash("Помилка: " + (d.error || ""));
    } catch (e) { flash("Помилка: " + e.message); }
    setAnalyzing(null);
  };

  const analyzeAll = async () => {
    if (analyzingAll) return;
    // Fetch all rows without a reason (paginate если нужно).
    let pending = [];
    try {
      const r = await apiFetch(`/api/projects/${activeId}/lost-backlinks/list?reason=not_checked&limit=500&sort=dr_desc`);
      const d = await r.json();
      pending = (d.items || []).map(x => x.id);
      // Iterate offset if total > 500
      let offset = 500;
      while (offset < (d.total || 0)) {
        const r2 = await apiFetch(`/api/projects/${activeId}/lost-backlinks/list?reason=not_checked&limit=500&offset=${offset}&sort=dr_desc`);
        const d2 = await r2.json();
        pending.push(...(d2.items || []).map(x => x.id));
        offset += 500;
      }
    } catch (e) { flash("Помилка: " + e.message); return; }

    if (!pending.length) { flash("Усі ссылки вже перевірено"); return; }
    if (!window.confirm(`Перевірити причини для ${pending.length} ссылок? Це може зайняти ~${Math.ceil(pending.length / 5 * 3 / 60)} хв (по 5 паралельно).`)) return;

    setAnalyzingAll(true);
    setBulkProgress({ current: 0, total: pending.length });
    const PARALLEL = 5;
    let done = 0;
    for (let i = 0; i < pending.length; i += PARALLEL) {
      const slice = pending.slice(i, i + PARALLEL);
      await Promise.all(slice.map(lbId =>
        apiFetch(`/api/projects/${activeId}/lost-backlinks/${lbId}/analyze-reason`, { method: "POST" })
          .catch(() => {})
      ));
      done += slice.length;
      setBulkProgress({ current: done, total: pending.length });
    }
    await loadReasons();
    await loadList();
    setAnalyzingAll(false);
    flash(`✓ Проаналізовано ${done} ссылок`);
  };

  const generateSuggestion = async (lbId) => {
    setGenerating(lbId);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/lost-backlinks/${lbId}/generate-suggestion`, { method: "POST" });
      const d = await r.json();
      if (d.ok) { flash("✓ AI-порада згенерована"); await loadList(); await loadTop(); }
      else flash("Помилка: " + (d.error || ""));
    } catch (e) { flash("Помилка: " + e.message); }
    setGenerating(null);
  };

  const updateStatus = async (lbId, newStatus, notes) => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/lost-backlinks/${lbId}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recovery_status: newStatus, ...(notes !== undefined ? { recovery_notes: notes } : {}) }),
      });
      if (r.ok) { await loadList(); await loadStatus(); flash("Збережено"); }
    } catch (e) { flash("Помилка: " + e.message); }
  };

  // ── Calendar heatmap data ───────────────────────────────────
  const heatmap = useMemo(() => {
    if (!calendar?.days?.length) return null;
    const map = new Map(calendar.days.map(d => [d.date, d]));
    const days = [];
    const today = new Date();
    for (let i = 89; i >= 0; i--) {
      const d = new Date(today); d.setDate(d.getDate() - i);
      const ds = d.toISOString().slice(0, 10);
      const data = map.get(ds);
      days.push({ date: ds, ...data });
    }
    return days;
  }, [calendar]);

  // ── Render ──────────────────────────────────────────────────
  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ ...stitle, margin: 0 }}>🔗❌ Втрачені посилання</h2>
        <p style={{ ...muted, margin: "4px 0 0", fontSize: 13 }}>
          Аналіз lost backlinks через Ahrefs API. Календар, причини, recovery tracker.
        </p>
      </div>

      {/* Status cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
        <Stat label="Live BL (Ahrefs)" value={status?.ahrefs?.live ?? "—"} />
        <Stat label="Live доменів" value={status?.ahrefs?.live_refdomains ?? "—"} />
        <Stat label="All-time BL" value={status?.ahrefs?.all_time ?? "—"} />
        <Stat label="Втрачено всього" value={status?.counts?.total ?? "—"} color="#ef4444" />
        <Stat label="High-DR (≥50)" value={status?.counts?.high_dr ?? "—"} color="#f59e0b" />
        <Stat label="За 30 днів" value={status?.counts?.lost_30d ?? "—"} />
        <Stat label="Відновлено" value={status?.counts?.recovered ?? "—"} color="#10b981" />
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
        {(() => {
          const done = (status?.counts?.total || 0) > 0;
          return (
            <>
              <button onClick={() => startBackfill(false)} disabled={pulling || done}
                style={{
                  ...btnP,
                  opacity: pulling ? 0.5 : (done ? 0.6 : 1),
                  cursor: (pulling || done) ? "not-allowed" : "pointer",
                  ...(done ? { background: "linear-gradient(135deg,#059669,#10b981)" } : {}),
                }}
                title={done
                  ? `Backfill виконано · ${status?.counts?.total} lost-посилань у БД. Для перезапуску — кнопка ↻ поруч.`
                  : "Витягнути всі коли-небудь існувавшие backlinks → визначити lost = all_time − live. ~3K Ahrefs units."}
              >
                {pulling ? "Завантажую..." : (done ? `✓ Backfill виконано (${status?.counts?.total})` : "🔄 Backfill (Ahrefs)")}
              </button>
              {done && (
                <button onClick={() => startBackfill(true)} disabled={pulling} style={{ ...btnSm }} title="Перезапустити backfill (нові додадуться, існуючі НЕ оновляться)">
                  ↻
                </button>
              )}
            </>
          );
        })()}
        <button onClick={() => exportCsv(30)} style={{ ...btnG, fontSize: 12 }} title="Експорт всіх втрачених за останні 30 днів у CSV">
          📥 CSV · 30 днів
        </button>
        <button onClick={() => exportCsv(90)} style={{ ...btnG, fontSize: 12 }} title="Експорт всіх втрачених за останні 90 днів у CSV">
          📥 CSV · 90 днів
        </button>
        <button onClick={openDisavow} style={{ ...btnG, fontSize: 12, color: "#ef4444", borderColor: "#ef4444" }} title="Витягнути всі спам-беклінки (Ahrefs is_spam) і обрати домени для disavow.txt">
          ⚠️ Disavow спам-доменів
        </button>
        {disavowOpen && (
          <div onClick={() => setDisavowOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, borderRadius: 12, width: "min(640px, 96vw)", maxHeight: "85vh", display: "flex", flexDirection: "column", overflow: "hidden", border: `1px solid ${t.border}` }}>
              <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: t.text }}>⚠️ Токсичні домени для disavow</span>
                {disavowRows && <span style={{ fontSize: 12, color: t.textMuted }}>{disavowRows.length} доменів{disavowRows.filter(x => x.harmful).length ? ` · ${disavowRows.filter(x => x.harmful).length} шкідливих` : ""}</span>}
                <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
                  {disavowCachedAt && <span style={{ fontSize: 11, color: t.textMuted }} title="Дані з кешу. Натисніть «Оновити» щоб перетягнути з Ahrefs заново.">Оновлено: {new Date(disavowCachedAt).toLocaleString("uk-UA")}</span>}
                  {disavowRows && <button onClick={() => openDisavow(true)} title="Перетягнути з Ahrefs заново (витратить units)" style={{ fontSize: 11, fontWeight: 600, padding: "4px 10px", borderRadius: 6, border: `1px solid ${t.border}`, background: "transparent", color: "#6366f1", cursor: "pointer" }}>🔄 Оновити</button>}
                  <button onClick={() => setDisavowOpen(false)} style={{ background: "none", border: "none", fontSize: 18, color: t.textMuted, cursor: "pointer" }}>×</button>
                </div>
              </div>
              {disavowRows === null ? (
                <div style={{ padding: 40, textAlign: "center", color: t.textMuted }}>Завантаження…</div>
              ) : disavowRows.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center", color: t.textMuted }}>Спам-доменів не знайдено 🎉</div>
              ) : (
                <>
                  <div style={{ padding: "8px 18px", borderBottom: `1px solid ${t.border}`, display: "flex", gap: 12, fontSize: 12, alignItems: "center" }}>
                    <button onClick={() => { const sel = {}; disavowRows.forEach(x => { sel[x.domain] = true; }); setDisavowSel(sel); }} style={{ background: "none", border: "none", color: "#6366f1", cursor: "pointer", fontWeight: 600 }}>Вибрати всі</button>
                    <button onClick={() => setDisavowSel({})} style={{ background: "none", border: "none", color: t.textMuted, cursor: "pointer", fontWeight: 600 }}>Зняти всі</button>
                    <span style={{ marginLeft: "auto", color: t.textMuted }}>Вибрано: {disavowRows.filter(x => disavowSel[x.domain]).length}</span>
                  </div>
                  <div style={{ overflowY: "auto", flex: 1 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr style={{ position: "sticky", top: 0, background: t.bgCard }}>
                          <th style={{ padding: "8px 12px", width: 36 }}></th>
                          <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 10, color: t.textMuted, textTransform: "uppercase" }}>Домен</th>
                          <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 10, color: t.textMuted, textTransform: "uppercase" }}>Причина</th>
                          <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 10, color: t.textMuted, textTransform: "uppercase", width: 70 }}>DR</th>
                          <th style={{ padding: "8px 12px", textAlign: "right", fontSize: 10, color: t.textMuted, textTransform: "uppercase", width: 120 }}>Трафік/міс</th>
                        </tr>
                      </thead>
                      <tbody>
                        {disavowRows.map(x => (
                          <tr key={x.domain} style={{ borderTop: `1px solid ${t.border}`, cursor: "pointer", background: x.harmful ? (dark ? "rgba(239,68,68,0.08)" : "rgba(239,68,68,0.05)") : "transparent" }} onClick={() => setDisavowSel(prev => ({ ...prev, [x.domain]: !prev[x.domain] }))}>
                            <td style={{ padding: "6px 12px", textAlign: "center" }}><input type="checkbox" checked={!!disavowSel[x.domain]} onChange={() => {}} /></td>
                            <td style={{ padding: "6px 12px", color: t.text }} title={x.category || ""}>{x.domain}</td>
                            <td style={{ padding: "6px 12px" }}><ReasonBadges cats={x.cats} reasons={x.reasons} /></td>
                            <td style={{ padding: "6px 12px", textAlign: "center" }}><DRBadge dr={x.dr} /></td>
                            <td style={{ padding: "6px 12px", textAlign: "right", color: t.textMuted }}>{x.traffic != null ? Number(x.traffic).toLocaleString() : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ padding: "12px 18px", borderTop: `1px solid ${t.border}`, display: "flex", justifyContent: "flex-end" }}>
                    <button onClick={downloadDisavowFile} style={{ ...btnP, fontSize: 13 }}>⬇ Завантажити disavow.txt ({disavowRows.filter(x => disavowSel[x.domain]).length})</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {task && (
          <span style={{ fontSize: 12, color: t.textMuted }}>
            {task.phase === "ahrefs_fetch" ? "Ahrefs API..." :
              task.phase === "done" ? `✓ all-time: ${task.allTime || 0} · live: ${task.live || 0} · lost: ${task.lost || 0} · inserted: ${task.inserted || 0}` :
              task.phase || ""}
          </span>
        )}
      </div>

      {/* Calendar heatmap */}
      {heatmap && (
        <div style={{ ...card, padding: "14px 18px", marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>
            КАЛЕНДАР (90 ДНІВ)
          </div>
          <Heatmap days={heatmap} />
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: 14, marginBottom: 14 }}>
        {/* Top-10 valuable */}
        <div style={{ ...card, padding: "14px 18px", minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>
            TOP-10 ЦІННИХ ВТРАЧЕНИХ <span style={{ fontWeight: 400, textTransform: "none", color: t.textMuted, fontSize: 10 }}>· score = DR + ln(traffic+10)×3</span>
          </div>
          {top10.length === 0 ? (
            <div style={{ fontSize: 12, color: t.textMuted, padding: 10 }}>Запустіть backfill для початку</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {top10.map(item => (
                <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px", borderRadius: 4, background: dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.015)" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: "0 0 auto" }}>
                    <DRBadge dr={item.domain_rating} />
                    {item.value_score != null && (
                      <span style={{ fontSize: 8, fontWeight: 700, padding: "1px 4px", borderRadius: 2, background: "rgba(139,92,246,.15)", color: "#8b5cf6", textAlign: "center" }} title="DR + log(traffic) value score">
                        {Math.round(item.value_score)}
                      </span>
                    )}
                  </div>
                  <div style={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.url_from}>{item.domain_from}</div>
                    <div style={{ fontSize: 10, color: t.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.anchor}>"{item.anchor || "—"}"</div>
                  </div>
                  <div style={{ fontSize: 10, color: t.textMuted, whiteSpace: "nowrap", flex: "0 0 auto", textAlign: "right" }}>
                    {item.traffic_domain ? `${(item.traffic_domain / 1000).toFixed(1)}K/міс` : "—"}
                    {item.collaborator && (
                      <div><CollabBadge c={item.collaborator} /></div>
                    )}
                  </div>
                  <button onClick={() => setSelected(item.id)} style={{ ...btnSm, fontSize: 10 }}>Деталі</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reasons distribution */}
        <div style={{ ...card, padding: "14px 18px", minWidth: 0, overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 10, gap: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
              ПРИЧИНИ ВТРАТИ
            </div>
            {(() => {
              const notChecked = reasons.find(r => r.reason === "not_checked")?.count || 0;
              if (notChecked > 0) {
                return (
                  <button onClick={() => analyzeAll()} disabled={analyzingAll}
                    style={{ ...btnSm, fontSize: 10, marginLeft: "auto", opacity: analyzingAll ? 0.5 : 1 }}
                    title={`Перевірити причини для ${notChecked} непроаналізованих ссылок (бекграунд)`}
                  >
                    {analyzingAll ? `${bulkProgress.current}/${bulkProgress.total}` : `🔍 Усі (${notChecked})`}
                  </button>
                );
              }
              return null;
            })()}
          </div>
          {reasons.length === 0 ? (
            <div style={{ fontSize: 12, color: t.textMuted, padding: 10 }}>Натисніть «🔍 Усі» щоб запустити аналіз</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {reasons.map(r => (
                <div key={r.reason} style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <div style={{ flex: "0 0 auto" }}><ReasonBadge reason={r.reason} /></div>
                  <div style={{ flex: 1, minWidth: 0, height: 6, background: dark ? "rgba(255,255,255,.05)" : "rgba(0,0,0,.05)", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${Math.min(100, (r.count / Math.max(...reasons.map(x => x.count))) * 100)}%`, background: reasonColor(r.reason) }} />
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, minWidth: 30, textAlign: "right", flex: "0 0 auto" }}>{r.count}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Full table with filters */}
      <div style={{ ...card, padding: "14px 18px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12, alignItems: "center" }}>
          <input type="text" placeholder="🔍 Пошук" value={filters.search}
            onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(0); }}
            style={{ ...inp, flex: 1, maxWidth: 300, fontSize: 12 }}
          />
          <select value={filters.status} onChange={e => { setFilters(f => ({ ...f, status: e.target.value })); setPage(0); }} style={{ ...inp, fontSize: 12 }}>
            <option value="">Всі статуси</option>
            <option value="new">Нові</option>
            <option value="contacted">Звернулись</option>
            <option value="working_on">В роботі</option>
            <option value="recovered">Відновлено</option>
            <option value="abandoned">Закрито</option>
          </select>
          <select value={filters.reason} onChange={e => { setFilters(f => ({ ...f, reason: e.target.value })); setPage(0); }} style={{ ...inp, fontSize: 12 }}>
            <option value="">Всі причини</option>
            <option value="page_404">404</option>
            <option value="anchor_removed">Анкор знято</option>
            <option value="nofollow_added">Стало nofollow</option>
            <option value="redirect_changed">Редирект</option>
            <option value="domain_dead">Domain мертвий</option>
            <option value="cf_blocked">CF blocked</option>
            <option value="false_positive">False positive</option>
            <option value="not_checked">Не перевірено</option>
          </select>
          <input type="number" placeholder="DR від" value={filters.dr_min} min={0} max={100}
            onChange={e => { setFilters(f => ({ ...f, dr_min: e.target.value })); setPage(0); }}
            style={{ ...inp, fontSize: 12, width: 90 }}
          />
          <select value={filters.sort} onChange={e => { setFilters(f => ({ ...f, sort: e.target.value })); setPage(0); }} style={{ ...inp, fontSize: 12 }}>
            <option value="dr_desc">DR ↓</option>
            <option value="dr_asc">DR ↑</option>
            <option value="lost_desc">Дата ↓</option>
            <option value="lost_asc">Дата ↑</option>
            <option value="traffic_desc">Трафік ↓</option>
          </select>
          <span style={{ fontSize: 11, color: t.textMuted, marginLeft: "auto" }}>{list.total} результатів</span>
        </div>

        <div style={{ overflowX: "auto", maxHeight: "60vh", overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ position: "sticky", top: 0, background: t.bgCard, zIndex: 1 }}>
                <Th>Donor</Th><Th width={50}>DR</Th><Th width={70}>Трафік</Th>
                <Th>Анкор</Th><Th>Target</Th><Th width={80}>Причина</Th>
                <Th width={80}>Статус</Th><Th width={100}>Втрачено</Th>
                <Th width={110}>🔵 Collab</Th>
                <Th width={80}></Th>
              </tr>
            </thead>
            <tbody>
              {list.items.map((item, i) => (
                <tr key={item.id} style={{
                  borderBottom: `1px solid ${t.border}`,
                  background: selected === item.id ? (dark ? "rgba(99,102,241,.12)" : "rgba(99,102,241,.06)") :
                              (i % 2 ? (dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.015)") : "transparent"),
                  cursor: "pointer",
                }} onClick={() => setSelected(item.id === selected ? null : item.id)}>
                  <Td maxw={220} title={item.url_from}>{item.domain_from}</Td>
                  <Td><DRBadge dr={item.domain_rating} /></Td>
                  <Td>{item.traffic_domain ? `${(item.traffic_domain / 1000).toFixed(1)}K` : "—"}</Td>
                  <Td maxw={180} title={item.anchor}>{trunc(item.anchor, 35)}</Td>
                  <Td maxw={200} title={item.url_to}>{shortPath(item.url_to)}</Td>
                  <Td><ReasonBadge reason={item.reason} /></Td>
                  <Td><StatusBadge s={item.recovery_status} /></Td>
                  <Td>{relTime(item.lost_at)}</Td>
                  <Td><CollabBadge c={item.collaborator} /></Td>
                  <Td>
                    <button onClick={(e) => { e.stopPropagation(); analyzeReason(item.id); }}
                      disabled={analyzing === item.id}
                      style={{ ...btnSm, fontSize: 10, opacity: analyzing === item.id ? 0.5 : 1 }}
                      title="Перевірити причину"
                    >{analyzing === item.id ? "..." : "🔍"}</button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 10, fontSize: 12 }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={btnSm}>‹</button>
          <span style={{ alignSelf: "center", color: t.textMuted }}>стор. {page + 1} з {Math.ceil(list.total / PAGE) || 1}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * PAGE >= list.total} style={btnSm}>›</button>
        </div>
      </div>

      {/* Detail panel */}
      {selected && (() => {
        const item = list.items.find(x => x.id === selected) || top10.find(x => x.id === selected);
        if (!item) return null;
        return (
          <div style={{ ...card, padding: "14px 18px", marginTop: 12 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
              <strong style={{ fontSize: 13 }}>{item.domain_from}</strong>
              <DRBadge dr={item.domain_rating} />
              {item.is_dofollow != null && (
                <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 3, background: item.is_dofollow ? "rgba(16,185,129,.12)" : "rgba(245,158,11,.12)", color: item.is_dofollow ? "#10b981" : "#f59e0b", fontWeight: 700 }}>
                  {item.is_dofollow ? "dofollow" : "nofollow"}
                </span>
              )}
              <button onClick={() => setSelected(null)} style={{ ...btnSm, marginLeft: "auto" }}>✕</button>
            </div>
            <DetailField label="URL Donor" value={item.url_from} link />
            <DetailField label="Анкор" value={item.anchor} />
            <DetailField label="Target URL" value={item.url_to} link />
            <DetailField label="Трафік donor" value={item.traffic_domain ? `${item.traffic_domain.toLocaleString()}/міс` : "—"} />
            <DetailField label="UR / Type" value={`UR ${item.url_rating || "—"} · ${item.link_type || "—"}`} />
            <DetailField label="Перша поява" value={item.first_seen_at ? new Date(item.first_seen_at).toLocaleString("uk-UA") : "—"} />
            <DetailField label="Остання поява live" value={item.last_seen_at ? new Date(item.last_seen_at).toLocaleString("uk-UA") : "—"} />
            <DetailField label="Втрачено" value={item.lost_at ? new Date(item.lost_at).toLocaleString("uk-UA") : "—"} />
            <DetailField label="Причина" value={item.reason || "не перевірено"} />

            {/* Collaborator match */}
            {item.collaborator && (
              <div style={{ marginTop: 12, padding: 12, borderRadius: 6, background: dark ? "rgba(14,165,233,.10)" : "rgba(14,165,233,.07)", border: `1px solid ${dark ? "rgba(14,165,233,.3)" : "rgba(14,165,233,.2)"}` }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 8, gap: 8, flexWrap: "wrap" }}>
                  <strong style={{ fontSize: 11, color: "#0ea5e9", textTransform: "uppercase", letterSpacing: 0.5 }}>🔵 Доступно на Collaborator</strong>
                  <a href={item.collaborator.url} target="_blank" rel="noreferrer"
                    style={{ ...btnSm, fontSize: 10, marginLeft: "auto", textDecoration: "none", color: "#0ea5e9", borderColor: "rgba(14,165,233,.3)" }}>
                    Перейти →
                  </a>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 8, fontSize: 12 }}>
                  <div><div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase" }}>Ціна від</div><div style={{ fontWeight: 700, color: "#0ea5e9" }}>{item.collaborator.price_min_uah ? `${Math.round(item.collaborator.price_min_uah).toLocaleString()} ₴` : "—"}</div></div>
                  <div><div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase" }}>DR</div><div style={{ fontWeight: 700 }}>{item.collaborator.dr || "—"}</div></div>
                  <div><div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase" }}>Organic</div><div style={{ fontWeight: 600 }}>{item.collaborator.traffic_num ? `${(item.collaborator.traffic_num / 1000).toFixed(1)}K/міс` : "—"}</div></div>
                  <div><div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase" }}>Країна</div><div style={{ fontWeight: 600 }}>{item.collaborator.country || "—"}</div></div>
                  <div><div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase" }}>Швидкість</div><div style={{ fontWeight: 600 }}>{item.collaborator.placement_speed || "—"}</div></div>
                  <div><div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase" }}>Link type</div><div style={{ fontWeight: 700, color: item.collaborator.has_dofollow ? "#10b981" : "#f59e0b" }}>{item.collaborator.has_dofollow ? "dofollow" : "nofollow"}</div></div>
                </div>
              </div>
            )}

            {/* AI suggestion */}
            <div style={{ marginTop: 12, padding: 10, borderRadius: 6, background: dark ? "rgba(99,102,241,.08)" : "rgba(99,102,241,.05)" }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
                <strong style={{ fontSize: 11, color: "#6366f1", textTransform: "uppercase", letterSpacing: 0.5 }}>💡 AI-порада з відновлення</strong>
                <button onClick={() => generateSuggestion(item.id)} disabled={generating === item.id}
                  style={{ ...btnSm, fontSize: 10, marginLeft: "auto", opacity: generating === item.id ? 0.5 : 1 }}>
                  {generating === item.id ? "..." : (item.recovery_suggestion ? "🔄 Перегенерувати" : "🤖 Згенерувати")}
                </button>
              </div>
              {item.recovery_suggestion ? (
                <div style={{ fontSize: 12, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{item.recovery_suggestion}</div>
              ) : (
                <div style={{ fontSize: 11, color: t.textMuted }}>Натисніть «Згенерувати» — gpt-4o-mini запропонує дії з recovery</div>
              )}
            </div>

            {/* Recovery status workflow */}
            <div style={{ marginTop: 12 }}>
              <strong style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>СТАТУС RECOVERY</strong>
              <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                {["new", "contacted", "working_on", "recovered", "abandoned"].map(s => (
                  <button key={s} onClick={() => updateStatus(item.id, s)}
                    style={{
                      ...btnSm, fontSize: 10,
                      ...(item.recovery_status === s ? {
                        background: statusColor(s) + "22",
                        color: statusColor(s),
                        borderColor: statusColor(s) + "55",
                        fontWeight: 700,
                      } : {}),
                    }}>
                    {statusLabel(s)}
                  </button>
                ))}
              </div>
              <textarea placeholder="Нотатки (контакт з donor, ETA, etc.)"
                defaultValue={item.recovery_notes || ""}
                onBlur={e => { if (e.target.value !== (item.recovery_notes || "")) updateStatus(item.id, item.recovery_status, e.target.value); }}
                style={{ ...inp, width: "100%", marginTop: 8, minHeight: 60, fontSize: 12 }}
              />
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────

function Stat({ label, value, color }) {
  const { t, card } = useApp();
  return (
    <div style={{ ...card, padding: "10px 12px" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: color || t.text, marginTop: 2 }}>{value ?? "—"}</div>
    </div>
  );
}

function Th({ children, width }) {
  const { t } = useApp();
  return <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.textMuted, borderBottom: `2px solid ${t.border}`, textTransform: "uppercase", whiteSpace: "nowrap", width: width || "auto" }}>{children}</th>;
}

function Td({ children, maxw, title }) {
  return <td style={{ padding: "6px 10px", maxWidth: maxw || "auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={title}>{children}</td>;
}

function CollabBadge({ c }) {
  if (!c) return <span style={{ fontSize: 10, color: "#888" }}>—</span>;
  const price = c.price_min_uah ? `${Math.round(c.price_min_uah).toLocaleString()}₴` : "?";
  return (
    <span
      title={`Collaborator · DR ${c.dr || "—"} · ${c.country || "?"} · ${c.placement_speed || "?"}${c.has_dofollow ? " · dofollow" : " · nofollow"}`}
      style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 3, background: "rgba(14,165,233,.15)", color: "#0ea5e9", whiteSpace: "nowrap" }}
    >
      🔵 {price}
    </span>
  );
}

function ReasonBadges({ cats, reasons }) {
  const META = {
    adult: { icon: "🔞", label: "Adult", color: "#ef4444" },
    gambling: { icon: "🎰", label: "Gambling", color: "#f59e0b" },
    loans: { icon: "💸", label: "Loans", color: "#f59e0b" },
    replica: { icon: "🧬", label: "Replica", color: "#a855f7" },
    pharma: { icon: "💊", label: "Pharma", color: "#06b6d4" },
    malware: { icon: "☠️", label: "Malware", color: "#dc2626" },
  };
  const cl = (cats || []);
  const rs = (reasons || []);
  const pill = (key, icon, label, color, title) => (
    <span key={key} title={title} style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 5, background: color + "22", color, whiteSpace: "nowrap" }}>{icon} {label}</span>
  );
  const badges = [];
  cl.forEach(c => { const m = META[c]; if (m) badges.push(pill(c, m.icon, m.label, m.color, rs.filter(r => r.endsWith(c)).join(", ") || c)); });
  if (rs.includes("spam")) badges.push(pill("spam", "🚩", "spam", "#ef4444", "Ahrefs is_spam"));
  if (rs.includes("pbn")) badges.push(pill("pbn", "🕸", "PBN", "#64748b", "DR 0 + 0 трафіку + 0 позицій"));
  if (!badges.length) badges.push(<span key="none" style={{ fontSize: 10, color: "#888" }}>—</span>);
  return <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>{badges}</span>;
}

function DRBadge({ dr }) {
  const v = Math.round(dr || 0);
  const bg = v >= 70 ? "#10b981" : v >= 50 ? "#f59e0b" : v >= 30 ? "#6366f1" : "#888";
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 3, background: bg + "22", color: bg, minWidth: 28, display: "inline-block", textAlign: "center" }}>
      {dr != null ? v : "—"}
    </span>
  );
}

function ReasonBadge({ reason }) {
  if (!reason || reason === "not_checked") {
    return <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 3, background: "rgba(0,0,0,.05)", color: "#888" }}>—</span>;
  }
  const c = reasonColor(reason);
  return <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 3, background: c + "22", color: c, whiteSpace: "nowrap" }}>{reasonLabel(reason)}</span>;
}

function StatusBadge({ s }) {
  const c = statusColor(s);
  return <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 3, background: c + "22", color: c, fontWeight: 600, whiteSpace: "nowrap" }}>{statusLabel(s)}</span>;
}

function DetailField({ label, value, link }) {
  const { t } = useApp();
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 12, wordBreak: "break-all" }}>
        {link && value ? <a href={value} target="_blank" rel="noreferrer" style={{ color: "#6366f1" }}>{value}</a> : (value || "—")}
      </div>
    </div>
  );
}

function Heatmap({ days }) {
  const { dark, t } = useApp();
  const max = Math.max(1, ...days.map(d => Math.abs((d?.backlinks_new || 0) - (d?.backlinks_lost || 0))));
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(45, 1fr)", gap: 2 }}>
      {days.map((d, i) => {
        const net = (d?.backlinks_new || 0) - (d?.backlinks_lost || 0);
        const intensity = Math.min(1, Math.abs(net) / max);
        const bg = !d || (!d.backlinks_new && !d.backlinks_lost) ? (dark ? "rgba(255,255,255,.05)" : "rgba(0,0,0,.04)") :
                   net > 0 ? `rgba(16,185,129,${0.2 + intensity * 0.7})` :
                   net < 0 ? `rgba(239,68,68,${0.2 + intensity * 0.7})` :
                   (dark ? "rgba(255,255,255,.1)" : "rgba(0,0,0,.08)");
        return (
          <div key={i} title={`${d.date}: +${d?.backlinks_new || 0} / −${d?.backlinks_lost || 0} (net ${net > 0 ? "+" : ""}${net})`}
            style={{ width: "100%", paddingBottom: "100%", background: bg, borderRadius: 2 }}
          />
        );
      })}
    </div>
  );
}

function reasonLabel(r) {
  return ({
    page_404: "404",
    anchor_removed: "знято",
    nofollow_added: "nofollow",
    redirect_changed: "редирект",
    redirect_to_self: "→ self",
    domain_dead: "мертвий",
    cf_blocked: "CF блок",
    false_positive: "live",
    donor_500: "500",
    donor_403: "403",
    unknown: "?",
    not_checked: "—",
  })[r] || r;
}
function reasonColor(r) {
  return ({
    page_404: "#ef4444",
    anchor_removed: "#f59e0b",
    nofollow_added: "#f59e0b",
    redirect_changed: "#6366f1",
    redirect_to_self: "#10b981",
    domain_dead: "#7c2d12",
    cf_blocked: "#888",
    false_positive: "#10b981",
    donor_500: "#ef4444",
    donor_403: "#888",
    unknown: "#888",
    not_checked: "#888",
  })[r] || "#888";
}
function statusLabel(s) {
  return ({
    new: "Нова",
    contacted: "Звернулись",
    working_on: "В роботі",
    recovered: "Відновлено",
    abandoned: "Закрито",
  })[s] || s || "—";
}
function statusColor(s) {
  return ({
    new: "#6366f1",
    contacted: "#f59e0b",
    working_on: "#8b5cf6",
    recovered: "#10b981",
    abandoned: "#888",
  })[s] || "#888";
}
function shortPath(url) {
  if (!url) return "";
  try { return new URL(url).pathname; } catch { return url; }
}
function trunc(s, n) {
  if (!s) return <span style={{ color: "#888" }}>—</span>;
  return s.length > n ? s.slice(0, n) + "…" : s;
}
function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "щойно";
  if (diff < 3600) return `${Math.floor(diff / 60)} хв тому`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} год тому`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)} дн. тому`;
  if (diff < 86400 * 365) return `${Math.floor(diff / 86400 / 30)} міс. тому`;
  return `${Math.floor(diff / 86400 / 365)} р. тому`;
}
